<script lang="ts">
    import { fade } from "svelte/transition";
    import { Booking, BookingAsset } from "../../model/Booking";
    import { Slot, SLOT_TYPES } from "../../model/Slot";
    import { calculateCardColsForSlotAssetCandidates } from "../../utility/CardUtility.svelte";
    import BootstrapVectorIcon from "../components/BootstrapVectorIcon.svelte";
    import AssetCandidateCard from "../components/AssetCandidateCard.svelte";
    import SlotInfoCard from "./SlotInfoCard.svelte";
    import BookingStatusBadge from "../components/BookingStatusBadge.svelte";
    import { DefaultErrorHandler } from "../../utility/ContextHelper.svelte";


    




    


    
    interface Props {
        bookingRequest: Booking;
        adSlot: Slot;
        /**
     * Enable or disable display of checkmark
     */
        showCheckMark?: boolean;
        keepImageCheckboxClicked?: (assetId: string) => void;
        /**
     * A list of assetIds for all items that should be checked
     */
        checkedItems?: string[];

        showStatus: boolean;
        /**
     * When true, only one asset per slot group can be selected at a time.
     * Used in the booking confirmation flow. Defaults to false (multiple allowed).
     */
        singleSelectPerSlot?: boolean;
    }

    let {
        bookingRequest,
        adSlot,
        showCheckMark = true,
        keepImageCheckboxClicked = undefined,
        checkedItems = [],
        showStatus = false,
        singleSelectPerSlot = false,
    }: Props = $props();

    let cardGridCols: number = $derived(adSlot ? calculateCardColsForSlotAssetCandidates(adSlot) : 3);

    /** All slots to display — single slot for simple bookings, subslots for complex. */
    let allSlots: Slot[] = $derived.by(() => {
        if (!adSlot) return [];
        if (adSlot.type === SLOT_TYPES.COMPLEX && adSlot.slots?.length) {
            return adSlot.slots;
        }
        return [adSlot];
    });

    /** Assets grouped by refSlot, preserving insertion order. */
    let assetsBySlot: Map<string, BookingAsset[]> = $derived.by(() => {
        const map = new Map<string, BookingAsset[]>();
        if (!bookingRequest?.assets) return map;
        for (const asset of bookingRequest.assets) {
            const key = asset.refSlot ?? adSlot?.id ?? '';
            if (!map.has(key)) map.set(key, []);
            map.get(key).push(asset);
        }
        return map;
    });

    /**
     * Returns a callback for a given slot group.
     * In singleSelectPerSlot mode, deselects the current selection in the
     * group before selecting the new one.
     */
    function makeSelectCallback(slotId: string): (assetId: string) => void {
        return function(assetId: string) {
            if (singleSelectPerSlot) {
                const groupAssets = assetsBySlot.get(slotId) ?? [];
                const alreadyChecked = groupAssets.find(
                    (a: BookingAsset) => a.id !== assetId && checkedItems.indexOf(a.id) >= 0
                );
                if (alreadyChecked && keepImageCheckboxClicked) {
                    keepImageCheckboxClicked(alreadyChecked.id);
                }
            }
            if (keepImageCheckboxClicked) {
                keepImageCheckboxClicked(assetId);
            }
        };
    }

    // Reevaluate impressions as budget get's changed
    let estimatedNumberOfImpressions = $derived(bookingRequest ? bookingRequest.volumeTotal / (30 / 1000) : 0);
    // Reestimate number of days as estimated impressions change
    let estimatedNumberOfDays = $derived(bookingRequest ? estimatedNumberOfImpressions / (130000 * 10) : 0);


    function getSlot(slotId: string): Slot {
        if(slotId == adSlot.id) {
            return adSlot;
        }

        if(!adSlot.slots) {
            DefaultErrorHandler("Internal error. Subslot information missing");
            return undefined;
        }

        let slot = adSlot.slots.find((s: Slot): boolean => {
            return slotId == s.id;
        });

        return slot;
    }
</script>

<div class="row mb-3">
    {#if adSlot}
        <SlotInfoCard class="col-12 col-xl-6" horizontal={window.screen.width > 1600} slot={adSlot}></SlotInfoCard>    
    {/if}
    <div class="col-12 col-xl-6">
        <div class="card h-100">
            <div class="card-body">

                <h3 class="text-center">Commercial Data</h3>
                <div class="row justify-content-center py-1">
                    <div class="col-5">
                        Name
                    </div>
                    <div class="col-5">
                        {bookingRequest.name}
                    </div>
                </div>
                <div class="row justify-content-center py-1">
                    <div class="col-5">
                        Brand
                    </div>
                    <div class="col-5">
                        {bookingRequest.brand}
                    </div>
                </div>
                <div class="row justify-content-center py-1">
                    <div class="col-5">
                        Budget
                    </div>
                    <div class="col-5">
                        {bookingRequest.volumeTotal.toLocaleString()} €
                    </div>
                </div>
                <div class="row justify-content-center py-1">
                    <div class="col-5">
                        Estimate
                    </div>
                    <div
                        id="estimatedImpressions"
                        class="col-5 justify-content-start"
                    >
                        {#if estimatedNumberOfImpressions}
                            {Math.round(
                                estimatedNumberOfImpressions,
                            ).toLocaleString()}&nbsp;<b>impressions</b
                            >&nbsp;over&nbsp;{Math.round(estimatedNumberOfDays * 100) /
                                100}&nbsp;<b>days</b>
                        {:else}
                            <BootstrapVectorIcon
                                style="height: 1em; width: 1em;"
                                icon="question"
                            ></BootstrapVectorIcon>
                        {/if}
                    </div>
                </div>
                
                <div class="row small justify-content-center py-2 border-top mt-2">
                    <div class="col-10">
                        <h2 class="h6 text-info text-uppercase mt-2">Conditions</h2>
                    </div>
                    <div class="col-5">
                        CPM
                    </div>
                    <div
                        class="col-5 justify-content-start"
                    >
                        30 €
                    </div>
                    <div class="col-5">
                        Your Revenue Share
                    </div>
                    <div
                        class="col-5 justify-content-start"
                    >
                        50 %
                    </div>
                    <div class="col-5">
                        Frequency Capping
                    </div>
                    <div
                        class="col-5 justify-content-start"
                    >
                        2 impressions per daily unique user
                    </div>
                    <div class="col-10 mt-2">
                        <a href="agb.pdf">General terms apply</a>
                    </div>
                </div>
                {#if showStatus}
                    <div class="row justify-content-center py-2">
                        <div class="col-5">
                            Status
                        </div>
                        <div class="col-5">
                            <BookingStatusBadge status={bookingRequest.status}></BookingStatusBadge>
                        </div>
                    </div>                    
                {/if}
            </div>
        </div>
    </div>
</div>

<div class="my-3" in:fade>
    <div class="card h-100 mt-6">
        <div class="card-body">

            <h2 class="text-center py-4">Your Asset Candidates</h2>
            {#each allSlots as slotInfo}
                {@const slotAssets = assetsBySlot.get(slotInfo.id) ?? []}
                {@const slotCols = calculateCardColsForSlotAssetCandidates(slotInfo)}
                <div class="row align-items-start mb-6 border-bottom pb-5">
                    <div class="col-12 col-xl-3 pe-xl-5 mb-4 mb-xl-0">
                        <SlotInfoCard slot={slotInfo} horizontal={false}></SlotInfoCard>
                    </div>
                    <div class="col-12 col-xl-9">
                        {#if slotAssets.length === 0}
                            <div class="text-info text-center py-4">
                                <BootstrapVectorIcon icon="exclamation-circle" class="me-2" style="height: 1.2em; width: 1.2em;"></BootstrapVectorIcon>
                                No candidates
                            </div>
                        {:else}
                            <div class="row">
                                {#each slotAssets as asset}
                                    <AssetCandidateCard
                                        assetId={asset.id}
                                        assetUrl={asset.url}
                                        cardGridCols={slotCols}
                                        showCheckMark={showCheckMark}
                                        checked={checkedItems.indexOf(asset.id) >= 0}
                                        keepImageCheckboxCallback={makeSelectCallback(slotInfo.id)}
                                        slot={slotInfo}
                                    ></AssetCandidateCard>
                                {/each}
                            </div>
                        {/if}
                    </div>
                </div>
            {/each}
        </div>
    </div>
</div>
